import React, { createContext, useContext, useMemo } from 'react';

import type { ConnectedAccount } from '@/hooks/usePlatformIntegrations';
import { usePlatformIntegrations } from '@/hooks/usePlatformIntegrations';
import type { PlatformType } from '@/services/socialAuth/types';

interface IntegrationContextType {
  // Connected accounts
  connectedAccounts: ConnectedAccount[];
  connectedPlatforms: string[];

  // Platform-specific connection status
  hasShopifyConnected: boolean;
  hasFacebookConnected: boolean;
  hasInstagramConnected: boolean;
  hasLinkedInConnected: boolean;
  hasTikTokConnected: boolean;

  // Account info (first connected account for each platform)
  shopifyAccount: ConnectedAccount | null;
  facebookAccounts: ConnectedAccount[];
  instagramAccounts: ConnectedAccount[];
  linkedInAccounts: ConnectedAccount[];
  tiktokAccounts: ConnectedAccount[];

  // Functions
  connectPlatform: (platform: PlatformType, identifier?: string) => Promise<void>;
  disconnectAccount: (platform: PlatformType, accountId: string) => Promise<void>;

  // Loading state
  isLoading: boolean;
  refreshConnections: () => void;
}

export const IntegrationContext = createContext<IntegrationContextType | null>(null);

export const IntegrationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    connectedAccounts,
    hasShopifyConnected,
    hasFacebookConnected,
    hasInstagramConnected,
    hasLinkedInConnected,
    hasTikTokConnected,
    connectedPlatforms,
    disconnectFunction,
    isLoading,
    refreshFunction,
  } = usePlatformIntegrations();

  // Filtered accounts by platform (memoized to prevent infinite re-renders)
  const shopifyAccount = useMemo(
    () => connectedAccounts.find((acc) => acc.platform === 'shopify') || null,
    [connectedAccounts],
  );
  const facebookAccounts = useMemo(
    () => connectedAccounts.filter((acc) => acc.platform === 'facebook'),
    [connectedAccounts],
  );
  const instagramAccounts = useMemo(
    () => connectedAccounts.filter((acc) => acc.platform === 'instagram'),
    [connectedAccounts],
  );
  const linkedInAccounts = useMemo(
    () => connectedAccounts.filter((acc) => acc.platform === 'linkedin'),
    [connectedAccounts],
  );
  const tiktokAccounts = useMemo(
    () => connectedAccounts.filter((acc) => acc.platform === 'tiktok'),
    [connectedAccounts],
  );

  // Connection status is now provided by the efficient hook

  // Deep-link into Settings instead of calling connectFunction/initiateAuth
  // directly. Phase B of OAuth (oauth-finalize-with-account) only runs from
  // useOAuthConnection's window "message" listener, and that hook is only
  // ever mounted by IntegrationConnectManager, which only renders inside
  // Settings surfaces. A consumer of this context can render anywhere, so
  // connectPlatform must never open the popup itself — it would complete
  // OAuth with the provider and then post its result into a void with zero
  // listeners. Same fix as PlatformPlugMenu, PlatformDisconnectedView,
  // ConnectedAccountsList, AnalyticsContextLine, and EngagementHub.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const connectPlatform = async (platform: PlatformType, _identifier?: string): Promise<void> => {
    window.dispatchEvent(
      new CustomEvent('mora:openSettings', {
        detail: { section: 'integrations', highlight: platform },
      }),
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const disconnectAccount = async (platform: PlatformType, accountId: string): Promise<void> => {
    await disconnectFunction(platform, accountId);
  };

  // Connected platforms array is now provided by the efficient hook

  const contextValue: IntegrationContextType = useMemo(
    () => ({
      connectedAccounts,
      connectedPlatforms,
      hasShopifyConnected,
      hasFacebookConnected,
      hasInstagramConnected,
      hasLinkedInConnected,
      hasTikTokConnected,
      shopifyAccount,
      facebookAccounts,
      instagramAccounts,
      linkedInAccounts,
      tiktokAccounts,
      connectPlatform,
      disconnectAccount,
      isLoading,
      refreshConnections: refreshFunction,
    }),
    [
      connectedAccounts,
      connectedPlatforms,
      hasShopifyConnected,
      hasFacebookConnected,
      hasInstagramConnected,
      hasLinkedInConnected,
      hasTikTokConnected,
      shopifyAccount,
      facebookAccounts,
      instagramAccounts,
      linkedInAccounts,
      tiktokAccounts,
      connectPlatform,
      disconnectAccount,
      isLoading,
      refreshFunction,
    ],
  );

  return <IntegrationContext.Provider value={contextValue}>{children}</IntegrationContext.Provider>;
};

export const useIntegrations = (): IntegrationContextType => {
  const context = useContext(IntegrationContext);
  if (!context) {
    throw new Error('useIntegrations must be used within an IntegrationProvider');
  }
  return context;
};
